import CloseButton from '@CS/assets/close.svg?component';
import { DELTA_UPDATE } from '@CS/utils/Number';
import { useClassNames } from '@CS/hooks';
type ClickEvent = MouseEvent<HTMLElement>;
type Child = JSX.Element | string | null;
type Children<Child> = Child | Child[];
import CSS from './Popup.module.css';

import {
  useRef,
  type JSX,
  useEffect,
  forwardRef,
  type MouseEvent,
  type ForwardedRef,
  type CSSProperties,
  useImperativeHandle
} from 'react';

export type PopupProps = {
  customStyle?: CSSProperties;
  onBeforeClose?: () => void;
  children?: Children<Child>;
  opacity?: number | string;

  onClose?: () => void;
  fullscreen?: boolean;
  hideClose?: boolean;
  className?: string;
  close?: boolean;
  width?: string;
};

export const Popup = forwardRef(
  (
    {
      onClose = () => void 0,
      fullscreen = false,
      hideClose = false,
      opacity = 0.25,
      close = false,
      width = '85%',

      onBeforeClose,
      customStyle,
      className,
      children
    }: PopupProps,
    ref: ForwardedRef<HTMLDialogElement>
  ) => {
    useImperativeHandle(ref, () => dialog.current!);
    const dialog = useRef<HTMLDialogElement>(null);

    const popupClassNames = useClassNames([
      fullscreen && CSS.fullscreen,
      CSS.popupContainer,
      className
    ]);

    const onClickOutside = (event: ClickEvent) =>
      !hideClose && event.target === event.currentTarget && onPopupClose();

    const onPopupClose = () => {
      const delay = +getComputedStyle(dialog.current!)
        .getPropertyValue('--transition-duration')
        .slice(0.0, -2.0);

      setTimeout(onClose, delay);
      dialog.current?.close();
      onBeforeClose?.();
    };

    useEffect(() => {
      close && onPopupClose();
    }, [close]);

    useEffect(() => {
      setTimeout(() => dialog.current?.showModal(), DELTA_UPDATE);
    }, []);

    return (
      <dialog
        ref={dialog}
        onClick={onClickOutside}
        className={popupClassNames}
        style={{ '--opacity': opacity, width } as CSSProperties}
      >
        {!hideClose && <CloseButton onClick={onPopupClose} />}
        <div className={CSS.popupContent} style={customStyle}>
          {children}
        </div>
      </dialog>
    );
  }
);
